import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, LogOut, Mail, Phone, Trash2, Filter, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";

const STATUS_LABELS = {
  neu: "Neu",
  in_bearbeitung: "In Bearbeitung",
  kontaktiert: "Kontaktiert",
  gewonnen: "Gewonnen",
  verloren: "Verloren",
};

const STATUS_COLORS = {
  neu: "bg-[#2C80C8]/10 text-[#2873B8]",
  in_bearbeitung: "bg-amber-100 text-amber-700",
  kontaktiert: "bg-slate-200 text-slate-700",
  gewonnen: "bg-emerald-100 text-emerald-700",
  verloren: "bg-rose-100 text-rose-700",
};

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("alle");
  const [selected, setSelected] = useState(null);

  const fetchData = async () => {
    setBusy(true);
    try {
      const [{ data: l }, { data: s }] = await Promise.all([
        api.get("/leads"),
        api.get("/leads/stats"),
      ]);
      setLeads(l);
      setStats(s);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white text-[#0C2749]">Laden …</div>;
  if (!user) return <Navigate to="/admin" replace />;

  const filteredLeads = filter === "alle" ? leads : leads.filter((l) => l.status === filter);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success("Status aktualisiert");
      fetchData();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Lead wirklich löschen?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead gelöscht");
      if (selected?.id === id) setSelected(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] text-[#0C2749]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <LogoMark size={36} />
            <div>
              <p className="text-sm font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
                <span className="text-[#2873B8]">Easy</span><span className="text-[#0C2749]">Rohbau</span>
                <span className="text-slate-400 font-normal"> · Admin</span>
              </p>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={fetchData} className="text-sm text-slate-600 hover:text-[#2873B8] inline-flex items-center gap-2" data-testid="admin-refresh">
              <RefreshCw className="w-4 h-4" /> Aktualisieren
            </button>
            <button onClick={logout} className="text-sm text-slate-600 hover:text-rose-600 inline-flex items-center gap-2" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Stat label="Gesamt" value={stats.total} testid="stat-total" />
            <Stat label="Letzte 7 Tage" value={stats.last_7_days} testid="stat-7d" />
            <Stat label="Neu" value={stats.by_status.neu || 0} testid="stat-neu" />
            <Stat label="In Bearbeitung" value={stats.by_status.in_bearbeitung || 0} testid="stat-progress" />
            <Stat label="Gewonnen" value={stats.by_status.gewonnen || 0} accent testid="stat-won" />
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>Leads</h2>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="text-sm border border-slate-200 px-3 py-1.5 rounded-sm bg-white"
                data-testid="admin-filter"
              >
                <option value="alle">{`Alle (${leads.length})`}</option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          {busy ? (
            <div className="py-16 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : filteredLeads.length === 0 ? (
            <div className="py-16 text-center text-slate-500 text-sm">Keine Leads gefunden.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <tr>
                    <th className="text-left px-6 py-3">Datum</th>
                    <th className="text-left px-6 py-3">Name</th>
                    <th className="text-left px-6 py-3">Kontakt</th>
                    <th className="text-left px-6 py-3">Quelle</th>
                    <th className="text-left px-6 py-3">Schätzung</th>
                    <th className="text-left px-6 py-3">Status</th>
                    <th className="text-right px-6 py-3">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLeads.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                      <td className="px-6 py-3 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("de-DE")}</td>
                      <td className="px-6 py-3 font-medium">{l.name}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3 text-xs">
                          <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-[#2873B8] hover:underline" onClick={(e) => e.stopPropagation()}><Mail className="w-3 h-3" /> {l.email}</a>
                          {l.telefon && <a href={`tel:${l.telefon}`} className="inline-flex items-center gap-1 text-[#2873B8] hover:underline" onClick={(e) => e.stopPropagation()}><Phone className="w-3 h-3" /> {l.telefon}</a>}
                        </div>
                      </td>
                      <td className="px-6 py-3 text-xs text-slate-500">{l.quelle}</td>
                      <td className="px-6 py-3 text-xs">
                        {l.estimate ? `${new Intl.NumberFormat("de-DE").format(l.estimate.total_low)} – ${new Intl.NumberFormat("de-DE").format(l.estimate.total_high)} €` : "—"}
                      </td>
                      <td className="px-6 py-3">
                        <select
                          value={l.status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-sm font-medium border-0 ${STATUS_COLORS[l.status] || ""}`}
                          data-testid={`lead-status-${l.id}`}
                        >
                          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </td>
                      <td className="px-6 py-3 text-right">
                        <button onClick={(e) => { e.stopPropagation(); removeLead(l.id); }} className="text-slate-400 hover:text-rose-600" data-testid={`lead-delete-${l.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selected && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={() => setSelected(null)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white text-[#0C2749] w-full max-w-2xl rounded-sm p-8 md:p-10" data-testid="admin-lead-detail">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#2873B8]">{selected.quelle}</p>
                  <h3 className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{selected.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{new Date(selected.created_at).toLocaleString("de-DE")}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-[#0C2749] text-2xl leading-none">×</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Info label="E-Mail" value={selected.email} />
                <Info label="Telefon" value={selected.telefon || "—"} />
                <Info label="Projekttyp" value={selected.projekttyp || "—"} />
                <Info label="Rückruf gewünscht" value={selected.rueckruf ? "Ja" : "Nein"} />
              </div>
              {selected.nachricht && (
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Nachricht</p>
                  <p className="text-sm text-slate-700 leading-relaxed bg-[#F5F5F5] p-4 rounded-sm whitespace-pre-wrap">{selected.nachricht}</p>
                </div>
              )}
              {selected.estimate && (
                <div className="mt-6 bg-grad-primary text-white p-6 rounded-sm">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#2C80C8] mb-2">KI-Schätzung</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
                    {new Intl.NumberFormat("de-DE").format(selected.estimate.total_low)} – {new Intl.NumberFormat("de-DE").format(selected.estimate.total_high)} €
                  </p>
                  <p className="text-sm text-white/80 mt-1">
                    ≈ {selected.estimate.price_per_m2_low} – {selected.estimate.price_per_m2_high} €/m²
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className={`p-5 rounded-sm border ${accent ? "bg-grad-primary text-white border-transparent" : "bg-white border-slate-200"}`} data-testid={testid}>
      <p className={`text-xs uppercase tracking-[0.2em] ${accent ? "text-[#2C80C8]" : "text-slate-500"}`}>{label}</p>
      <p className="text-3xl font-bold mt-2" style={{ fontFamily: "Outfit" }}>{value}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</p>
      <p className="text-[#0C2749] font-medium">{value}</p>
    </div>
  );
}
